import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, addMonths, getMonth, getYear } from 'date-fns';
import type { DreData, DreSummary, DreSummaryByBu, DreGrandTotals, DreLineItem, DreSection, SourceDetail } from './useDreRealizado';
import { useSalesGoalsProjection } from '@/hooks/useSalesGoals';
import { calculateMonthlySalaryCostUSD } from '@/lib/salaryCalculations';

// Extended section type for projections - DRE structure (includes deductions and csp which are projected-only)
interface DreSectionExtended {
  name: string;
  icon: string;
  type: 'revenue' | 'deductions' | 'csp' | 'cac' | 'ga' | 'financial';
  items: DreLineItem[];
  subtotal: Record<string, number>;
  subtotalByBu?: {
    us: Record<string, number>;
    br: Record<string, number>;
  };
  total: number;
  totalByBu?: {
    us: number;
    br: number;
  };
}

// Extended DreData for projections with proper DRE structure
export interface DreDataProjetado {
  sections: DreSectionExtended[];
  months: string[];
  summary: DreSummary;
  summaryByBu?: DreSummaryByBu;
  grandTotals: {
    revenue: number;
    grossRevenue: number;
    deductions: number;
    netRevenue: number;
    csp: number;
    grossProfit: number;
    cac: number;
    marginContribution: number;
    ga: number;
    ebitda: number;
    financial: number;
    netProfit: number;
    grossMargin: number;
    marginContributionPercent: number;
    ebitdaMargin: number;
    netMargin: number;
  };
  grandTotalsByBu?: {
    us: DreGrandTotals;
    br: DreGrandTotals;
  };
  revenueRatios?: {
    us: number;
    br: number;
  };
}

export interface ForecastScenarioMultipliers {
  id: string;
  name: string;
  description: string | null;
  revenue_multiplier: number;
  cac_multiplier: number;
  fulfillment_multiplier: number;
  ga_multiplier: number;
  financial_multiplier: number;
  monthly_revenue_growth: number;
  monthly_expense_growth: number;
  default_rate: number;
  is_default: boolean;
}

interface UseDreProjetadoParams {
  horizonMonths: number;
  scenarioId?: string;
  businessUnit?: string;
  pddRate?: number;
  cancellationRate?: number;
}

// Map department/role to account_code
function getDefaultAccountCode(department?: string, role?: string): string {
  const dept = (department || '').toLowerCase();
  const r = (role || '').toLowerCase();
  
  if (dept.includes('sales') || r.includes('sdr') || r.includes('closer') || r.includes('sales')) {
    return '5200';
  }
  if (dept.includes('legal') || r.includes('attorney') || r.includes('paralegal')) {
    return '6100';
  }
  if (dept.includes('management') || r.includes('ceo') || r.includes('coo') || r.includes('cfo')) {
    return '7100';
  }
  if (dept.includes('marketing') || r.includes('marketing')) {
    return '4200';
  }
  return '7500';
}

export function useForecastScenariosWithMultipliers() {
  return useQuery({
    queryKey: ['forecast-scenarios-multipliers'],
    queryFn: async (): Promise<ForecastScenarioMultipliers[]> => {
      const { data, error } = await supabase
        .from('forecast_scenarios')
        .select('id, name, description, revenue_multiplier, cac_multiplier, fulfillment_multiplier, ga_multiplier, financial_multiplier, monthly_revenue_growth, monthly_expense_growth, default_rate, is_default')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      
      return (data || []).map(s => ({
        ...s,
        revenue_multiplier: Number(s.revenue_multiplier) || 1,
        cac_multiplier: Number(s.cac_multiplier) || 1,
        fulfillment_multiplier: Number(s.fulfillment_multiplier) || 1,
        ga_multiplier: Number(s.ga_multiplier) || 1,
        financial_multiplier: Number(s.financial_multiplier) || 1,
        monthly_revenue_growth: Number(s.monthly_revenue_growth) || 0.03,
        monthly_expense_growth: Number(s.monthly_expense_growth) || 0.02,
        default_rate: Number(s.default_rate) || 0.05,
      }));
    },
  });
}

type BuKey = 'us' | 'br';

function createEmptySummary(): DreSummary {
  return {
    totalRevenue: {},
    totalCac: {},
    totalFulfillment: {},
    totalGa: {},
    totalFinancial: {},
    grossProfit: {},
    marginContribution: {},
    ebitda: {},
    netProfit: {},
    grossMargin: {},
    marginContributionPercent: {},
    ebitdaMargin: {},
    netMargin: {},
  };
}

function createEmptyGrandTotals(): DreGrandTotals {
  return {
    revenue: 0,
    cac: 0,
    fulfillment: 0,
    ga: 0,
    financial: 0,
    grossProfit: 0,
    marginContribution: 0,
    ebitda: 0,
    netProfit: 0,
    grossMargin: 0,
    marginContributionPercent: 0,
    ebitdaMargin: 0,
    netMargin: 0,
  };
}

export function useDreProjetado({ horizonMonths, scenarioId, businessUnit, pddRate = 0.30, cancellationRate = 0.10 }: UseDreProjetadoParams) {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < horizonMonths; i++) {
    const date = addMonths(now, i);
    months.push(format(date, 'yyyy-MM'));
  }
  
  // Fetch sales goals separately for US and BR to enable BU breakdown
  const { data: salesGoalsMapUs } = useSalesGoalsProjection(months, 'usa');
  const { data: salesGoalsMapBr } = useSalesGoalsProjection(months, 'brasil');
  
  return useQuery({
    queryKey: ['dre-projetado', horizonMonths, scenarioId, businessUnit, pddRate, cancellationRate, 
      salesGoalsMapUs ? Array.from(salesGoalsMapUs.entries()) : [],
      salesGoalsMapBr ? Array.from(salesGoalsMapBr.entries()) : []
    ],
    queryFn: async (): Promise<DreData> => {
      // 1. Get scenario multipliers
      let scenario: ForecastScenarioMultipliers | null = null;
      
      if (scenarioId) {
        const { data } = await supabase
          .from('forecast_scenarios')
          .select('id, name, description, revenue_multiplier, cac_multiplier, fulfillment_multiplier, ga_multiplier, financial_multiplier, monthly_revenue_growth, monthly_expense_growth, default_rate, is_default')
          .eq('id', scenarioId)
          .single();
        
        if (data) {
          scenario = {
            ...data,
            revenue_multiplier: Number(data.revenue_multiplier) || 1,
            cac_multiplier: Number(data.cac_multiplier) || 1,
            fulfillment_multiplier: Number(data.fulfillment_multiplier) || 1,
            ga_multiplier: Number(data.ga_multiplier) || 1,
            financial_multiplier: Number(data.financial_multiplier) || 1,
            monthly_revenue_growth: Number(data.monthly_revenue_growth) || 0.03,
            monthly_expense_growth: Number(data.monthly_expense_growth) || 0.02,
            default_rate: Number(data.default_rate) || 0.05,
          };
        }
      } else {
        const { data } = await supabase
          .from('forecast_scenarios')
          .select('id, name, description, revenue_multiplier, cac_multiplier, fulfillment_multiplier, ga_multiplier, financial_multiplier, monthly_revenue_growth, monthly_expense_growth, default_rate, is_default')
          .eq('is_default', true)
          .single();
        
        if (data) {
          scenario = {
            ...data,
            revenue_multiplier: Number(data.revenue_multiplier) || 1,
            cac_multiplier: Number(data.cac_multiplier) || 1,
            fulfillment_multiplier: Number(data.fulfillment_multiplier) || 1,
            ga_multiplier: Number(data.ga_multiplier) || 1,
            financial_multiplier: Number(data.financial_multiplier) || 1,
            monthly_revenue_growth: Number(data.monthly_revenue_growth) || 0.03,
            monthly_expense_growth: Number(data.monthly_expense_growth) || 0.02,
            default_rate: Number(data.default_rate) || 0.05,
          };
        }
      }
      
      if (!scenario) {
        scenario = {
          id: 'default',
          name: 'Base',
          description: null,
          revenue_multiplier: 1,
          cac_multiplier: 1,
          fulfillment_multiplier: 1,
          ga_multiplier: 1,
          financial_multiplier: 1,
          monthly_revenue_growth: 0.03,
          monthly_expense_growth: 0.02,
          default_rate: 0.05,
          is_default: true,
        };
      }
      
      const startYear = now.getFullYear();
      
      // Fetch all data sources in parallel (no BU filter - we need all for separation)
      const fetchBudgetEntries = async () => {
        const { data } = await supabase
          .from('budget_entries')
          .select('*, account:chart_of_accounts!budget_entries_account_code_fkey(code, name, type)')
          .gte('year', startYear)
          .lte('year', startYear + 2);
        return data || [];
      };
      
      const fetchRecurringExpenses = async () => {
        const { data } = await supabase
          .from('recurring_expenses')
          .select('id, account_code, business_unit, name, amount, currency, cost_type, is_active')
          .eq('is_active', true);
        return data || [];
      };
      
      const fetchEmployees = async () => {
        const { data } = await supabase
          .from('employees')
          .select('id, full_name, role, department, account_code, base_salary, salary_currency, benefits_cost, status, business_unit, salary_frequency, weekly_payment_type, hourly_rate, projected_hours_per_week, payment_day_of_week')
          .eq('status', 'active');
        return data || [];
      };
      
      const fetchAccounts = async () => {
        const { data } = await supabase
          .from('chart_of_accounts')
          .select('*')
          .order('code');
        return data || [];
      };
      
      const fetchHistory = async () => {
        const { data } = await supabase
          .from('expense_history')
          .select('*')
          .order('year', { ascending: false })
          .order('month', { ascending: false })
          .limit(100);
        return data || [];
      };
      
      const fetchExchangeRate = async () => {
        const { data } = await supabase
          .from('exchange_rates')
          .select('rate')
          .eq('from_currency', 'BRL')
          .eq('to_currency', 'USD')
          .order('effective_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        return data?.rate || null;
      };
      
      const [budgetEntries, recurringExpenses, employees, accounts, expenseHistory, brlToUsdRate] = await Promise.all([
        fetchBudgetEntries(),
        fetchRecurringExpenses(),
        fetchEmployees(),
        fetchAccounts(),
        fetchHistory(),
        fetchExchangeRate(),
      ]);
      
      const exchangeRate = brlToUsdRate ? (1 / brlToUsdRate) : 5.5;
      const accountMap = new Map(accounts?.map(a => [a.code, a]) || []);
      
      // Initialize sections with BU breakdown
      const sections: DreSectionExtended[] = [
        { name: 'Receita Bruta', icon: '📈', type: 'revenue', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: `(-) Deduções da Receita`, icon: '⚠️', type: 'deductions', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) CSP - Custo dos Serviços Prestados', icon: '📦', type: 'csp', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) CAC - Custo de Aquisição', icon: '📣', type: 'cac', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) G&A - Despesas Administrativas', icon: '🏢', type: 'ga', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) Despesas Financeiras', icon: '💳', type: 'financial', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
      ];
      
      const sectionMap: Record<string, number> = {
        revenue: 0,
        fulfillment: 2,
        cac: 3,
        ga: 4,
        financial: 5,
      };
      
      // Group budget by account code, month, and BU
      const budgetByAccountMonthBu: Record<string, Record<string, Record<BuKey | 'shared', { amount: number; details: SourceDetail[] }>>> = {};
      
      budgetEntries?.forEach(entry => {
        const code = entry.account_code || '7500';
        const monthKey = `${entry.year}-${String(entry.month).padStart(2, '0')}`;
        const bu: BuKey | 'shared' = entry.country === 'us' ? 'us' : entry.country === 'br' ? 'br' : 'shared';
        
        if (!budgetByAccountMonthBu[code]) budgetByAccountMonthBu[code] = {};
        if (!budgetByAccountMonthBu[code][monthKey]) {
          budgetByAccountMonthBu[code][monthKey] = { us: { amount: 0, details: [] }, br: { amount: 0, details: [] }, shared: { amount: 0, details: [] } };
        }
        
        const amount = Number(entry.planned_amount);
        budgetByAccountMonthBu[code][monthKey][bu].amount += amount;
        budgetByAccountMonthBu[code][monthKey][bu].details.push({
          sourceType: 'budget',
          sourceId: entry.id,
          sourceName: entry.notes || `Budget ${monthKey}`,
          amount,
        });
      });
      
      // Group recurring expenses by account code and BU
      const recurringByAccountBu: Record<string, Record<BuKey | 'shared', { amount: number; details: SourceDetail[] }>> = {};
      
      recurringExpenses?.forEach(re => {
        const code = re.account_code || '7500';
        const bu: BuKey | 'shared' = re.business_unit === 'usa' ? 'us' : re.business_unit === 'brasil' ? 'br' : 'shared';
        const amount = Number(re.amount) || 0;
        
        if (!recurringByAccountBu[code]) {
          recurringByAccountBu[code] = { us: { amount: 0, details: [] }, br: { amount: 0, details: [] }, shared: { amount: 0, details: [] } };
        }
        
        recurringByAccountBu[code][bu].amount += amount;
        recurringByAccountBu[code][bu].details.push({
          sourceType: 'recurring_expense',
          sourceId: re.id,
          sourceName: re.name,
          amount,
        });
      });
      
      // Group employee costs by account code, BU, and month (for proper weekly/biweekly calculation)
      const currentYear = getYear(now);
      const currentMonth = getMonth(now);
      
      // For each month in the projection, we need to calculate the proper salary cost
      const employeesByAccountBuMonth: Record<string, Record<BuKey | 'shared', Record<string, { amount: number; details: SourceDetail[] }>>> = {};
      
      employees?.forEach(emp => {
        const code = emp.account_code || getDefaultAccountCode(emp.department, emp.role);
        const bu: BuKey | 'shared' = emp.business_unit === 'usa' ? 'us' : emp.business_unit === 'brasil' ? 'br' : 'shared';
        
        if (!employeesByAccountBuMonth[code]) {
          employeesByAccountBuMonth[code] = { us: {}, br: {}, shared: {} };
        }
        
        // Calculate for each month in the projection
        months.forEach((monthKey) => {
          const [year, month] = monthKey.split('-').map(Number);
          const monthlyCost = calculateMonthlySalaryCostUSD(emp, year, month - 1, exchangeRate);
          
          if (!employeesByAccountBuMonth[code][bu][monthKey]) {
            employeesByAccountBuMonth[code][bu][monthKey] = { amount: 0, details: [] };
          }
          
          employeesByAccountBuMonth[code][bu][monthKey].amount += monthlyCost;
          employeesByAccountBuMonth[code][bu][monthKey].details.push({
            sourceType: 'employee',
            sourceId: emp.id,
            sourceName: emp.full_name,
            amount: monthlyCost,
          });
        });
      });
      
      // Note: employeesByAccountBuMonth now stores per-month values for accurate weekly/biweekly calculations
      
      // Historical averages (no BU breakdown, used as fallback)
      const historicalAvgByAccount: Record<string, number> = {};
      const historicalCountByAccount: Record<string, number> = {};
      
      expenseHistory?.forEach(h => {
        const code = h.account_code || '7500';
        historicalAvgByAccount[code] = (historicalAvgByAccount[code] || 0) + Number(h.amount || 0);
        historicalCountByAccount[code] = (historicalCountByAccount[code] || 0) + 1;
      });
      
      Object.keys(historicalAvgByAccount).forEach(code => {
        const count = historicalCountByAccount[code] || 1;
        historicalAvgByAccount[code] = historicalAvgByAccount[code] / Math.max(count, 1);
      });
      
      // Calculate revenue by BU first (needed for shared expense allocation)
      let totalUsRevenue = 0;
      let totalBrRevenue = 0;
      
      months.forEach((m, idx) => {
        const usGoal = salesGoalsMapUs?.get(m) || 0;
        const brGoal = salesGoalsMapBr?.get(m) || 0;
        const growthFactor = Math.pow(1 + scenario!.monthly_revenue_growth, idx);
        
        totalUsRevenue += usGoal * scenario!.revenue_multiplier * growthFactor;
        totalBrRevenue += brGoal * scenario!.revenue_multiplier * growthFactor;
      });
      
      const totalRevenue = totalUsRevenue + totalBrRevenue;
      const usRatio = totalRevenue > 0 ? totalUsRevenue / totalRevenue : 0.5;
      const brRatio = totalRevenue > 0 ? totalBrRevenue / totalRevenue : 0.5;
      
      // Build revenue section with BU breakdown
      const revenueAmounts: Record<string, number> = {};
      const revenueAmountsByBu: { us: Record<string, number>; br: Record<string, number> } = { us: {}, br: {} };
      const revenueSourceDetails: Record<string, SourceDetail[]> = {};
      
      months.forEach((m, idx) => {
        const usGoal = salesGoalsMapUs?.get(m) || 0;
        const brGoal = salesGoalsMapBr?.get(m) || 0;
        const growthFactor = Math.pow(1 + scenario!.monthly_revenue_growth, idx);
        
        const usAmount = usGoal * scenario!.revenue_multiplier * growthFactor;
        const brAmount = brGoal * scenario!.revenue_multiplier * growthFactor;
        
        revenueAmountsByBu.us[m] = usAmount;
        revenueAmountsByBu.br[m] = brAmount;
        revenueAmounts[m] = usAmount + brAmount;
        
        sections[0].subtotal[m] = usAmount + brAmount;
        sections[0].subtotalByBu!.us[m] = usAmount;
        sections[0].subtotalByBu!.br[m] = brAmount;
        
        revenueSourceDetails[m] = [
          { sourceType: 'sales_goal', sourceId: `sales-goal-us-${m}`, sourceName: 'Meta de Vendas (USA)', amount: usAmount },
          { sourceType: 'sales_goal', sourceId: `sales-goal-br-${m}`, sourceName: 'Meta de Vendas (BR)', amount: brAmount },
        ];
      });
      
      sections[0].total = Object.values(revenueAmounts).reduce((a, b) => a + b, 0);
      sections[0].totalByBu!.us = Object.values(revenueAmountsByBu.us).reduce((a, b) => a + b, 0);
      sections[0].totalByBu!.br = Object.values(revenueAmountsByBu.br).reduce((a, b) => a + b, 0);
      
      sections[0].items.push({
        accountCode: 'SALES',
        accountName: 'Receita de Vendas (Metas)',
        category: 'revenue',
        amounts: revenueAmounts,
        amountsByBu: revenueAmountsByBu,
        total: sections[0].total,
        totalByBu: { us: sections[0].totalByBu!.us, br: sections[0].totalByBu!.br },
        type: 'revenue',
        sourceDetails: revenueSourceDetails,
      });
      
      // Build deductions section with BU breakdown
      const pddAmounts: Record<string, number> = {};
      const pddAmountsByBu: { us: Record<string, number>; br: Record<string, number> } = { us: {}, br: {} };
      const cancelAmounts: Record<string, number> = {};
      const cancelAmountsByBu: { us: Record<string, number>; br: Record<string, number> } = { us: {}, br: {} };
      
      months.forEach(m => {
        const usRev = sections[0].subtotalByBu!.us[m] || 0;
        const brRev = sections[0].subtotalByBu!.br[m] || 0;
        
        pddAmountsByBu.us[m] = usRev * pddRate;
        pddAmountsByBu.br[m] = brRev * pddRate;
        pddAmounts[m] = pddAmountsByBu.us[m] + pddAmountsByBu.br[m];
        
        cancelAmountsByBu.us[m] = usRev * cancellationRate;
        cancelAmountsByBu.br[m] = brRev * cancellationRate;
        cancelAmounts[m] = cancelAmountsByBu.us[m] + cancelAmountsByBu.br[m];
        
        sections[1].subtotal[m] = pddAmounts[m] + cancelAmounts[m];
        sections[1].subtotalByBu!.us[m] = pddAmountsByBu.us[m] + cancelAmountsByBu.us[m];
        sections[1].subtotalByBu!.br[m] = pddAmountsByBu.br[m] + cancelAmountsByBu.br[m];
      });
      
      const totalPdd = Object.values(pddAmounts).reduce((a, b) => a + b, 0);
      const totalCancel = Object.values(cancelAmounts).reduce((a, b) => a + b, 0);
      
      sections[1].total = totalPdd + totalCancel;
      sections[1].totalByBu!.us = Object.values(pddAmountsByBu.us).reduce((a, b) => a + b, 0) + Object.values(cancelAmountsByBu.us).reduce((a, b) => a + b, 0);
      sections[1].totalByBu!.br = Object.values(pddAmountsByBu.br).reduce((a, b) => a + b, 0) + Object.values(cancelAmountsByBu.br).reduce((a, b) => a + b, 0);
      
      if (totalPdd > 0) {
        sections[1].items.push({
          accountCode: 'PDD',
          accountName: `PDD - Inadimplência (${(pddRate * 100).toFixed(0)}%)`,
          category: 'deductions',
          amounts: pddAmounts,
          amountsByBu: pddAmountsByBu,
          total: totalPdd,
          totalByBu: { 
            us: Object.values(pddAmountsByBu.us).reduce((a, b) => a + b, 0), 
            br: Object.values(pddAmountsByBu.br).reduce((a, b) => a + b, 0) 
          },
          type: 'expense',
        });
      }
      
      if (totalCancel > 0) {
        sections[1].items.push({
          accountCode: 'CANCEL',
          accountName: `Cancelamentos (${(cancellationRate * 100).toFixed(0)}%)`,
          category: 'deductions',
          amounts: cancelAmounts,
          amountsByBu: cancelAmountsByBu,
          total: totalCancel,
          totalByBu: { 
            us: Object.values(cancelAmountsByBu.us).reduce((a, b) => a + b, 0), 
            br: Object.values(cancelAmountsByBu.br).reduce((a, b) => a + b, 0) 
          },
          type: 'expense',
        });
      }
      
      // Process expense accounts with BU breakdown
        const allAccountCodes = new Set([
          ...Object.keys(budgetByAccountMonthBu),
          ...Object.keys(recurringByAccountBu),
          ...Object.keys(employeesByAccountBuMonth),
          ...Object.keys(historicalAvgByAccount),
        ]);
      
      allAccountCodes.forEach(code => {
        const account = accountMap.get(code);
        const accountType = account?.type || 'ga';
        if (accountType === 'revenue') return; // Skip revenue accounts - handled above
        
        const sectionIdx = sectionMap[accountType] ?? 4;
        
        let multiplier = 1;
        switch (accountType) {
          case 'cac': multiplier = scenario!.cac_multiplier; break;
          case 'fulfillment': multiplier = scenario!.fulfillment_multiplier; break;
          case 'ga': multiplier = scenario!.ga_multiplier; break;
          case 'financial': multiplier = scenario!.financial_multiplier; break;
        }
        
        const amounts: Record<string, number> = {};
        const amountsByBu: { us: Record<string, number>; br: Record<string, number> } = { us: {}, br: {} };
        const sourceDetails: Record<string, SourceDetail[]> = {};
        
        const recurringData = recurringByAccountBu[code];
        const historicalAmount = historicalAvgByAccount[code] || 0;
        
        months.forEach((m, idx) => {
          const growthRate = scenario!.monthly_expense_growth;
          const growthFactor = Math.pow(1 + growthRate, idx);
          
          const budgetData = budgetByAccountMonthBu[code]?.[m];
          
          let usAmount = 0;
          let brAmount = 0;
          const details: SourceDetail[] = [];
          
          if (budgetData && (budgetData.us.amount > 0 || budgetData.br.amount > 0 || budgetData.shared.amount > 0)) {
            // Use budget with BU breakdown
            usAmount = budgetData.us.amount * multiplier;
            brAmount = budgetData.br.amount * multiplier;
            // Allocate shared budget proportionally
            usAmount += budgetData.shared.amount * multiplier * usRatio;
            brAmount += budgetData.shared.amount * multiplier * brRatio;
            
            details.push(...budgetData.us.details.map(d => ({ ...d, amount: d.amount * multiplier })));
            details.push(...budgetData.br.details.map(d => ({ ...d, amount: d.amount * multiplier })));
            details.push(...budgetData.shared.details.map(d => ({ ...d, amount: d.amount * multiplier })));
          } else if (recurringData || employeesByAccountBuMonth[code]) {
            // Use recurring + employees with BU breakdown
            // Recurring expenses use growth factor
            const usRecurring = (recurringData?.us.amount || 0) * growthFactor * multiplier;
            const brRecurring = (recurringData?.br.amount || 0) * growthFactor * multiplier;
            const sharedRecurring = (recurringData?.shared.amount || 0) * growthFactor * multiplier;
            
            // Employee costs use the actual monthly calculated value (no growth factor - calendar-based)
            const employeeMonthData = employeesByAccountBuMonth[code];
            const usEmployee = (employeeMonthData?.us?.[m]?.amount || 0) * multiplier;
            const brEmployee = (employeeMonthData?.br?.[m]?.amount || 0) * multiplier;
            const sharedEmployee = (employeeMonthData?.shared?.[m]?.amount || 0) * multiplier;
            
            usAmount = usRecurring + usEmployee + (sharedRecurring + sharedEmployee) * usRatio;
            brAmount = brRecurring + brEmployee + (sharedRecurring + sharedEmployee) * brRatio;
            
            if (recurringData) {
              details.push(...recurringData.us.details.map(d => ({ ...d, amount: d.amount * growthFactor * multiplier })));
              details.push(...recurringData.br.details.map(d => ({ ...d, amount: d.amount * growthFactor * multiplier })));
              details.push(...recurringData.shared.details.map(d => ({ ...d, amount: d.amount * growthFactor * multiplier })));
            }
            if (employeeMonthData) {
              // Use the actual month's details (already calculated with correct calendar)
              if (employeeMonthData.us?.[m]?.details) {
                details.push(...employeeMonthData.us[m].details.map(d => ({ ...d, amount: d.amount * multiplier })));
              }
              if (employeeMonthData.br?.[m]?.details) {
                details.push(...employeeMonthData.br[m].details.map(d => ({ ...d, amount: d.amount * multiplier })));
              }
              if (employeeMonthData.shared?.[m]?.details) {
                details.push(...employeeMonthData.shared[m].details.map(d => ({ ...d, amount: d.amount * multiplier })));
              }
            }
          } else if (historicalAmount > 0) {
            // Fallback to historical - allocate proportionally
            const total = historicalAmount * growthFactor * multiplier;
            usAmount = total * usRatio;
            brAmount = total * brRatio;
            details.push({
              sourceType: 'history',
              sourceId: `history-${code}-${m}`,
              sourceName: 'Média Histórica',
              amount: total,
            });
          }
          
          amountsByBu.us[m] = usAmount;
          amountsByBu.br[m] = brAmount;
          amounts[m] = usAmount + brAmount;
          sourceDetails[m] = details;
        });
        
        const total = Object.values(amounts).reduce((a, b) => a + b, 0);
        const totalUs = Object.values(amountsByBu.us).reduce((a, b) => a + b, 0);
        const totalBr = Object.values(amountsByBu.br).reduce((a, b) => a + b, 0);
        
        if (total > 0) {
          const item: DreLineItem = {
            accountCode: code,
            accountName: account?.name || code,
            category: accountType,
            amounts,
            amountsByBu,
            total,
            totalByBu: { us: totalUs, br: totalBr },
            type: 'expense',
            sourceDetails,
          };
          
          sections[sectionIdx].items.push(item);
          sections[sectionIdx].total += total;
          sections[sectionIdx].totalByBu!.us += totalUs;
          sections[sectionIdx].totalByBu!.br += totalBr;
          
          months.forEach(m => {
            sections[sectionIdx].subtotal[m] = (sections[sectionIdx].subtotal[m] || 0) + (amounts[m] || 0);
            sections[sectionIdx].subtotalByBu!.us[m] = (sections[sectionIdx].subtotalByBu!.us[m] || 0) + (amountsByBu.us[m] || 0);
            sections[sectionIdx].subtotalByBu!.br[m] = (sections[sectionIdx].subtotalByBu!.br[m] || 0) + (amountsByBu.br[m] || 0);
          });
        }
      });
      
      // Sort items within each section
      sections.forEach(s => s.items.sort((a, b) => a.accountCode.localeCompare(b.accountCode)));
      
      // Calculate summary (consolidated)
      const summary: DreSummary = createEmptySummary();
      const summaryUs: DreSummary = createEmptySummary();
      const summaryBr: DreSummary = createEmptySummary();
      
      months.forEach(m => {
        // Consolidated
        const grossRevenue = sections[0].subtotal[m] || 0;
        const deductions = sections[1].subtotal[m] || 0;
        const netRevenue = grossRevenue - deductions;
        const csp = sections[2].subtotal[m] || 0;
        const grossProfit = netRevenue - csp;
        const cac = sections[3].subtotal[m] || 0;
        const marginContribution = grossProfit - cac;
        const ga = sections[4].subtotal[m] || 0;
        const ebitda = marginContribution - ga;
        const financial = sections[5].subtotal[m] || 0;
        const netProfit = ebitda - financial;
        
        summary.totalRevenue[m] = netRevenue;
        summary.totalFulfillment[m] = csp;
        summary.totalCac[m] = cac;
        summary.totalGa[m] = ga;
        summary.totalFinancial[m] = financial;
        summary.grossProfit[m] = grossProfit;
        summary.marginContribution[m] = marginContribution;
        summary.ebitda[m] = ebitda;
        summary.netProfit[m] = netProfit;
        summary.grossMargin[m] = grossRevenue > 0 ? (grossProfit / grossRevenue) * 100 : 0;
        summary.marginContributionPercent[m] = grossRevenue > 0 ? (marginContribution / grossRevenue) * 100 : 0;
        summary.ebitdaMargin[m] = grossRevenue > 0 ? (ebitda / grossRevenue) * 100 : 0;
        summary.netMargin[m] = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;
        
        // US
        const grossRevenueUs = sections[0].subtotalByBu!.us[m] || 0;
        const deductionsUs = sections[1].subtotalByBu!.us[m] || 0;
        const netRevenueUs = grossRevenueUs - deductionsUs;
        const cspUs = sections[2].subtotalByBu!.us[m] || 0;
        const grossProfitUs = netRevenueUs - cspUs;
        const cacUs = sections[3].subtotalByBu!.us[m] || 0;
        const marginContributionUs = grossProfitUs - cacUs;
        const gaUs = sections[4].subtotalByBu!.us[m] || 0;
        const ebitdaUs = marginContributionUs - gaUs;
        const financialUs = sections[5].subtotalByBu!.us[m] || 0;
        const netProfitUs = ebitdaUs - financialUs;
        
        summaryUs.totalRevenue[m] = netRevenueUs;
        summaryUs.totalFulfillment[m] = cspUs;
        summaryUs.totalCac[m] = cacUs;
        summaryUs.totalGa[m] = gaUs;
        summaryUs.totalFinancial[m] = financialUs;
        summaryUs.grossProfit[m] = grossProfitUs;
        summaryUs.marginContribution[m] = marginContributionUs;
        summaryUs.ebitda[m] = ebitdaUs;
        summaryUs.netProfit[m] = netProfitUs;
        summaryUs.grossMargin[m] = grossRevenueUs > 0 ? (grossProfitUs / grossRevenueUs) * 100 : 0;
        summaryUs.marginContributionPercent[m] = grossRevenueUs > 0 ? (marginContributionUs / grossRevenueUs) * 100 : 0;
        summaryUs.ebitdaMargin[m] = grossRevenueUs > 0 ? (ebitdaUs / grossRevenueUs) * 100 : 0;
        summaryUs.netMargin[m] = grossRevenueUs > 0 ? (netProfitUs / grossRevenueUs) * 100 : 0;
        
        // BR
        const grossRevenueBr = sections[0].subtotalByBu!.br[m] || 0;
        const deductionsBr = sections[1].subtotalByBu!.br[m] || 0;
        const netRevenueBr = grossRevenueBr - deductionsBr;
        const cspBr = sections[2].subtotalByBu!.br[m] || 0;
        const grossProfitBr = netRevenueBr - cspBr;
        const cacBr = sections[3].subtotalByBu!.br[m] || 0;
        const marginContributionBr = grossProfitBr - cacBr;
        const gaBr = sections[4].subtotalByBu!.br[m] || 0;
        const ebitdaBr = marginContributionBr - gaBr;
        const financialBr = sections[5].subtotalByBu!.br[m] || 0;
        const netProfitBr = ebitdaBr - financialBr;
        
        summaryBr.totalRevenue[m] = netRevenueBr;
        summaryBr.totalFulfillment[m] = cspBr;
        summaryBr.totalCac[m] = cacBr;
        summaryBr.totalGa[m] = gaBr;
        summaryBr.totalFinancial[m] = financialBr;
        summaryBr.grossProfit[m] = grossProfitBr;
        summaryBr.marginContribution[m] = marginContributionBr;
        summaryBr.ebitda[m] = ebitdaBr;
        summaryBr.netProfit[m] = netProfitBr;
        summaryBr.grossMargin[m] = grossRevenueBr > 0 ? (grossProfitBr / grossRevenueBr) * 100 : 0;
        summaryBr.marginContributionPercent[m] = grossRevenueBr > 0 ? (marginContributionBr / grossRevenueBr) * 100 : 0;
        summaryBr.ebitdaMargin[m] = grossRevenueBr > 0 ? (ebitdaBr / grossRevenueBr) * 100 : 0;
        summaryBr.netMargin[m] = grossRevenueBr > 0 ? (netProfitBr / grossRevenueBr) * 100 : 0;
      });
      
      // Calculate grand totals (consolidated)
      const totalGrossRevenue = sections[0].total;
      const totalDeductions = sections[1].total;
      const totalNetRevenue = totalGrossRevenue - totalDeductions;
      const totalCsp = sections[2].total;
      const totalGrossProfit = totalNetRevenue - totalCsp;
      const totalCac = sections[3].total;
      const totalMarginContribution = totalGrossProfit - totalCac;
      const totalGa = sections[4].total;
      const totalEbitda = totalMarginContribution - totalGa;
      const totalFinancial = sections[5].total;
      const totalNetProfit = totalEbitda - totalFinancial;
      
      // Grand totals by BU
      const calcGrandTotals = (bu: BuKey): DreGrandTotals => {
        const rev = sections[0].totalByBu![bu];
        const ded = sections[1].totalByBu![bu];
        const netRev = rev - ded;
        const csp = sections[2].totalByBu![bu];
        const gp = netRev - csp;
        const cac = sections[3].totalByBu![bu];
        const mc = gp - cac;
        const ga = sections[4].totalByBu![bu];
        const ebit = mc - ga;
        const fin = sections[5].totalByBu![bu];
        const np = ebit - fin;
        
        return {
          revenue: rev,
          cac,
          fulfillment: csp,
          ga,
          financial: fin,
          grossProfit: gp,
          marginContribution: mc,
          ebitda: ebit,
          netProfit: np,
          grossMargin: rev > 0 ? (gp / rev) * 100 : 0,
          marginContributionPercent: rev > 0 ? (mc / rev) * 100 : 0,
          ebitdaMargin: rev > 0 ? (ebit / rev) * 100 : 0,
          netMargin: rev > 0 ? (np / rev) * 100 : 0,
        };
      };
      
      return {
        sections: sections as unknown as DreData['sections'],
        months,
        summary,
        summaryByBu: {
          us: summaryUs,
          br: summaryBr,
        },
        grandTotals: {
          revenue: totalGrossRevenue,
          grossRevenue: totalGrossRevenue,
          deductions: totalDeductions,
          netRevenue: totalNetRevenue,
          csp: totalCsp,
          grossProfit: totalGrossProfit,
          cac: totalCac,
          marginContribution: totalMarginContribution,
          ga: totalGa,
          ebitda: totalEbitda,
          financial: totalFinancial,
          netProfit: totalNetProfit,
          grossMargin: totalGrossRevenue > 0 ? (totalGrossProfit / totalGrossRevenue) * 100 : 0,
          marginContributionPercent: totalGrossRevenue > 0 ? (totalMarginContribution / totalGrossRevenue) * 100 : 0,
          ebitdaMargin: totalGrossRevenue > 0 ? (totalEbitda / totalGrossRevenue) * 100 : 0,
          netMargin: totalGrossRevenue > 0 ? (totalNetProfit / totalGrossRevenue) * 100 : 0,
        },
        grandTotalsByBu: {
          us: calcGrandTotals('us'),
          br: calcGrandTotals('br'),
        },
        revenueRatios: {
          us: usRatio,
          br: brRatio,
        },
      } as unknown as DreData;
    },
  });
}
